/**
 * MFASettings. lets a logged-in user manage their MFA method.
 * Supports: Email OTP (existing) and TOTP (Google/Microsoft Authenticator).
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Paper, Button, Alert, CircularProgress,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stepper, Step, StepLabel, Divider, Radio,
  RadioGroup, FormControlLabel,
} from "@mui/material";
import ShieldIcon        from "@mui/icons-material/Shield";
import EmailIcon         from "@mui/icons-material/Email";
import PhoneAndroidIcon  from "@mui/icons-material/PhoneAndroid";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import QrCode2Icon       from "@mui/icons-material/QrCode2";
import api from "../utils/api";

// ── 6-digit code input (reuse same pattern as MFAVerify) ──────────────────────
function CodeInput({ value, onChange, disabled }) {
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? "");
  return (
    <Box sx={{ display:"flex", gap:1, justifyContent:"center", mt:1 }}
      onClick={() => inputRef.current?.focus()}>
      {digits.map((d, i) => (
        <Box key={i} sx={{
          width:44, height:52, border:`2px solid ${value.length===i?"#2f63ad":"rgba(47,99,173,0.25)"}`,
          borderRadius:"10px", display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:"1.4rem", fontWeight:700, color:"#223142",
          backgroundColor: value.length===i ? "rgba(47,99,173,0.04)" : "#fff",
        }}>{d}</Box>
      ))}
      <input ref={inputRef} value={value} disabled={disabled}
        onChange={e => { const v=e.target.value.replace(/\D/g,'').slice(0,6); onChange(v); }}
        style={{ position:'absolute', opacity:0, width:1, height:1 }} />
    </Box>
  );
}

// ── TOTP Setup Dialog ─────────────────────────────────────────────────────────
function TotpSetupDialog({ open, onClose, onSuccess }) {
  const [step,     setStep]     = useState(0); // 0=scan, 1=verify, 2=done
  const [loading,  setLoading]  = useState(false);
  const [secret,   setSecret]   = useState("");
  const [qrCode,   setQrCode]   = useState("");
  const [code,     setCode]     = useState("");
  const [err,      setErr]       = useState("");

  useEffect(() => {
    if (!open) { setStep(0); setCode(""); setErr(""); return; }
    // Generate secret when dialog opens
    setLoading(true);
    api.post("/mfa/totp/setup")
      .then(r => { setSecret(r.data.secret); setQrCode(r.data.qrCode); })
      .catch(() => setErr("Failed to generate QR code. Please try again."))
      .finally(() => setLoading(false));
  }, [open]);

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setLoading(true); setErr("");
    try {
      await api.post("/mfa/totp/activate", { secret, code });
      setStep(2);
    } catch (e) {
      setErr(e.response?.data?.message || "Invalid code. Make sure your device clock is correct.");
      setCode("");
    } finally { setLoading(false); }
  };

  const STEPS = ["Scan QR Code", "Verify Setup", "Done"];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:"18px" } }}>
      <DialogTitle sx={{ fontWeight:700, pb:0 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
          <PhoneAndroidIcon sx={{ color:"#2f63ad" }} />
          Set Up Authenticator App
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mt:1.5, mb:2.5 }} alternativeLabel>
          {STEPS.map(l => <Step key={l}><StepLabel sx={{ "& .MuiStepLabel-label":{ fontSize:"0.72rem" } }}>{l}</StepLabel></Step>)}
        </Stepper>

        {step === 0 && (
          <Box sx={{ textAlign:"center" }}>
            <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
              Scan this QR code with <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong>, or any TOTP app.
            </Typography>
            {loading ? <CircularProgress /> :
              qrCode ? <img src={qrCode} alt="QR Code" style={{ width:200, height:200, border:"1px solid #e0e7ef", borderRadius:12 }} />
              : err ? <Alert severity="error" sx={{ borderRadius:"10px" }}>{err}</Alert> : null}
            {secret && (
              <Box sx={{ mt:2, p:1.5, borderRadius:"10px", backgroundColor:"rgba(47,99,173,0.05)", border:"1px solid rgba(47,99,173,0.12)" }}>
                <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:0.5 }}>
                  Can't scan? Enter this key manually:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily:"monospace", fontWeight:700, color:"#223142", letterSpacing:1, wordBreak:"break-all" }}>
                  {secret.match(/.{1,4}/g)?.join(' ')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ textAlign:"center" }}>
            <QrCode2Icon sx={{ fontSize:48, color:"rgba(47,99,173,0.3)", mb:1 }} />
            <Typography variant="body2" sx={{ color:"#6b778c", mb:1 }}>
              Enter the <strong>6-digit code</strong> currently shown in your authenticator app to confirm the setup worked.
            </Typography>
            <CodeInput value={code} onChange={setCode} disabled={loading} />
            {err && <Alert severity="error" sx={{ mt:1.5, borderRadius:"10px", fontSize:"0.78rem" }}>{err}</Alert>}
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ textAlign:"center", py:2 }}>
            <CheckCircleIcon sx={{ fontSize:56, color:"#16a34a", mb:1.5, display:"block", mx:"auto" }} />
            <Typography variant="h6" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>Authenticator App Active</Typography>
            <Typography variant="body2" sx={{ color:"#6b778c" }}>
              Your account is now protected with your authenticator app. You'll enter a code from the app each time you log in.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
        {step < 2 && <Button onClick={onClose} sx={{ textTransform:"none", color:"#6b778c" }}>Cancel</Button>}
        {step === 0 && (
          <Button variant="contained" onClick={() => setStep(1)} disabled={!qrCode || loading}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, background:"#2f63ad" }}>
            I've Scanned It
          </Button>
        )}
        {step === 1 && (
          <Button variant="contained" onClick={handleVerify} disabled={code.length!==6||loading}
            startIcon={loading?<CircularProgress size={16} color="inherit"/>:undefined}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, background:"#2f63ad" }}>
            {loading?"Verifying…":"Verify & Enable"}
          </Button>
        )}
        {step === 2 && (
          <Button variant="contained" onClick={() => { onSuccess(); onClose(); }}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, background:"#16a34a" }}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function MFASettings() {
  const [status,  setStatus]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]      = useState("");
  const [success, setSuccess]  = useState("");
  const [totpOpen,setTotpOpen] = useState(false);

  const loadStatus = () => {
    setLoading(true);
    api.get("/mfa/status")
      .then(r => setStatus(r.data))
      .catch(() => setErr("Failed to load MFA settings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStatus(); }, []);

  const handleDisableTotp = async (switchToEmail) => {
    setSaving(true); setErr(""); setSuccess("");
    try {
      await api.post("/mfa/totp/disable", { switchToEmail });
      setSuccess(switchToEmail ? "Switched back to email verification." : "Authenticator app removed.");
      loadStatus();
    } catch (e) { setErr(e.response?.data?.message || "Failed to update MFA"); }
    finally { setSaving(false); }
  };

  const handleToggleEmailMfa = async () => {
    setSaving(true); setErr(""); setSuccess("");
    try {
      await api.put("/mfa/toggle");
      loadStatus();
    } catch (e) { setErr(e.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ py:3, textAlign:"center" }}><CircularProgress size={24} /></Box>;

  const isTotp   = status?.mfa_method === "totp" && status?.totp_verified;
  const isEmail  = status?.mfa_enabled && !isTotp;
  const mfaOff   = !status?.mfa_enabled;

  return (
    <Box>
      <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:2 }}>
        <ShieldIcon sx={{ color:"#2f63ad", fontSize:24 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>Two-Factor Authentication</Typography>
          <Typography variant="caption" sx={{ color:"#6b778c" }}>Add an extra layer of security to your account</Typography>
        </Box>
        {status?.mfa_enabled && (
          <Chip label={isTotp?"Authenticator App":"Email"} size="small"
            icon={isTotp?<PhoneAndroidIcon sx={{fontSize:"14px!important"}}/>:<EmailIcon sx={{fontSize:"14px!important"}}/>}
            sx={{ ml:"auto", fontWeight:700, fontSize:"0.7rem",
              backgroundColor:isTotp?"rgba(22,163,74,0.12)":"rgba(47,99,173,0.10)",
              color:isTotp?"#15803d":"#2f63ad" }} />
        )}
      </Box>

      {err     && <Alert severity="error"   sx={{ mb:2, borderRadius:"10px" }}>{err}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2, borderRadius:"10px" }}>{success}</Alert>}

      <Divider sx={{ mb:2.5 }} />

      {/* Option cards */}
      <Box sx={{ display:"flex", flexDirection:"column", gap:1.5 }}>

        {/* Email OTP */}
        <Paper elevation={0} sx={{
          p:2, borderRadius:"14px",
          border:`2px solid ${isEmail?"#2f63ad":"rgba(47,99,173,0.10)"}`,
          backgroundColor:isEmail?"rgba(47,99,173,0.04)":"#fafbff",
        }}>
          <Box sx={{ display:"flex", alignItems:"flex-start", gap:1.5 }}>
            <EmailIcon sx={{ color:isEmail?"#2f63ad":"#9aa5b4", mt:0.25 }} />
            <Box sx={{ flex:1 }}>
              <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>Email Verification</Typography>
              <Typography variant="caption" sx={{ color:"#6b778c" }}>
                A 6-digit code is sent to{" "}
                <strong>{status?.email_masked || "your email"}</strong> each login.
                Simple. no app required.
              </Typography>
            </Box>
            {isEmail && <CheckCircleIcon sx={{ color:"#2f63ad", flexShrink:0 }} />}
          </Box>
          {mfaOff && (
            <Button size="small" variant="outlined" onClick={handleToggleEmailMfa} disabled={saving}
              sx={{ mt:1.5, borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:"0.75rem" }}>
              Enable Email MFA
            </Button>
          )}
          {isTotp && (
            <Button size="small" variant="outlined" onClick={() => handleDisableTotp(true)} disabled={saving}
              sx={{ mt:1.5, borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:"0.75rem", color:"#6b778c", borderColor:"rgba(0,0,0,0.15)" }}>
              Switch to Email Instead
            </Button>
          )}
          {isEmail && (
            <Button size="small" variant="text" color="error" onClick={handleToggleEmailMfa} disabled={saving}
              sx={{ mt:1, borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:"0.72rem" }}>
              Disable MFA
            </Button>
          )}
        </Paper>

        {/* Authenticator App (TOTP) */}
        <Paper elevation={0} sx={{
          p:2, borderRadius:"14px",
          border:`2px solid ${isTotp?"#16a34a":"rgba(47,99,173,0.10)"}`,
          backgroundColor:isTotp?"rgba(22,163,74,0.04)":"#fafbff",
        }}>
          <Box sx={{ display:"flex", alignItems:"flex-start", gap:1.5 }}>
            <PhoneAndroidIcon sx={{ color:isTotp?"#16a34a":"#9aa5b4", mt:0.25 }} />
            <Box sx={{ flex:1 }}>
              <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:0.25 }}>
                <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>Authenticator App</Typography>
                <Chip label="Recommended" size="small" sx={{ height:16, fontSize:"0.58rem", fontWeight:700, backgroundColor:"rgba(22,163,74,0.12)", color:"#15803d" }} />
              </Box>
              <Typography variant="caption" sx={{ color:"#6b778c" }}>
                Use <strong>Google Authenticator</strong> or <strong>Microsoft Authenticator</strong>.
                Works offline. more secure than email.
              </Typography>
            </Box>
            {isTotp && <CheckCircleIcon sx={{ color:"#16a34a", flexShrink:0 }} />}
          </Box>

          {!isTotp && (
            <Button size="small" variant="contained" onClick={() => setTotpOpen(true)} disabled={saving}
              sx={{ mt:1.5, borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:"0.75rem",
                background:"#2f63ad", "&:hover":{ background:"#1e4c8a" } }}>
              Set Up Authenticator App
            </Button>
          )}
          {isTotp && (
            <Button size="small" variant="text" color="error" onClick={() => handleDisableTotp(false)} disabled={saving}
              sx={{ mt:1, borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:"0.72rem" }}>
              Remove Authenticator App
            </Button>
          )}
        </Paper>
      </Box>

      <TotpSetupDialog open={totpOpen} onClose={() => setTotpOpen(false)}
        onSuccess={() => { setSuccess("Authenticator app enabled!"); loadStatus(); }} />
    </Box>
  );
}
